import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import PhotoUpload from "@/components/PhotoUpload";
import { ShieldCheck, ShieldAlert, Clock, X, Flag, Truck, PackageCheck, Package, ExternalLink, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { SentimentPicker } from "@/components/SellerRep";
import { REPORT_REASONS, trackingUrl } from "@/lib/taxonomy";

const CLAIM_COPY = {
  assessing: { label: "Claim under AI review", color: "#2563EB" },
  reassessing: { label: "Re-reviewing with seller's response", color: "#2563EB" },
  approved: { label: "Refund approved", color: "#16A34A" },
  denied: { label: "Claim denied", color: "#E11D48" },
};

const STEPS = [
  { key: "paid", label: "Paid", Icon: Package },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "delivered", label: "Delivered", Icon: PackageCheck },
];

const RETURN_STATUS_LABEL = {
  PRE_TRANSIT: "Label created — drop it off with the carrier",
  TRANSIT: "In transit back to the seller",
  DELIVERED: "Delivered back to the seller",
  RETURNED: "Returned to sender",
  FAILURE: "Delivery issue — contact the carrier",
  UNKNOWN: "Awaiting first scan",
};

function windowLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  return h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h left` : `${h}h ${Math.floor((ms % 3600000) / 60000)}m left`;
}

function Timeline({ order }) {
  const fs = order.fulfilment_status || (order.guarantee_expires_at ? "delivered" : "awaiting_shipment");
  const reached = { awaiting_shipment: 0, shipped: 1, delivered: 2 }[fs] ?? 0;
  return (
    <div className="flex items-center gap-2" data-testid={`timeline-${order.id}`} data-stage={fs}>
      {STEPS.map((s, i) => {
        const on = i <= reached;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className="h-px w-6" style={{ background: on ? "#1E3A2B" : "#DFDCD6" }} />}
            <span className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.14em]"
              style={{ color: on ? "#1E3A2B" : "#9AA096" }}>
              <s.Icon size={12} /> {s.label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [claimFor, setClaimFor] = useState(null);
  const [form, setForm] = useState({ reason: REPORT_REASONS[0], note: "", evidence_paths: [] });
  const [sending, setSending] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [review, setReview] = useState({ sentiment: "good", comment: "" });
  const [savingReview, setSavingReview] = useState(false);
  const [returnLoading, setReturnLoading] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const pending = orders.some((o) => ["assessing", "reassessing"].includes(o.claim?.status));
    if (!pending) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [orders]);

  const confirmReceipt = async (id) => {
    try {
      await api.post(`/orders/${id}/received`);
      toast.success("Delivery confirmed — your 3-day authenticity guarantee starts now");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const fileClaim = async () => {
    setSending(true);
    try {
      await api.post(`/orders/${claimFor.id}/claim`, form);
      toast.success("Claim filed — the seller's payout stays on hold while our AI reviews it");
      setClaimFor(null);
      setForm({ reason: REPORT_REASONS[0], note: "", evidence_paths: [] });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const submitReview = async () => {
    setSavingReview(true);
    try {
      await api.post(`/orders/${reviewFor.id}/review`, review);
      toast.success("Thanks — your review is now live on the seller's items");
      setReviewFor(null); setReview({ sentiment: "good", comment: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingReview(false); }
  };

  const buyReturnLabel = async (id) => {
    setReturnLoading(id);
    try {
      const { data } = await api.post(`/orders/${id}/return-label`);
      toast.success(`Return label ready — ${data.carrier} ${data.tracking_number}`);
      if (data.label_url) window.open(data.label_url, "_blank");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setReturnLoading(null); }
  };

  return (
    <div data-testid="orders-page" className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6">
        <div className="label-eyebrow">Buyer Protection</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Purchases.</h1>
        <p className="mt-3 text-sm text-neutral-500 max-w-xl">
          Track every parcel here. The 3-day authenticity guarantee starts the moment your item is delivered — the
          seller's payout stays on hold until the window closes.
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="hairline p-16 text-center mt-10">
          <div className="label-eyebrow">No purchases yet</div>
          <Link to="/browse" className="btn-primary inline-flex mt-6">Start browsing</Link>
        </div>
      ) : (
        <ul className="mt-10 divide-y divide-black/10">
          {orders.map((o) => {
            const delivered = o.fulfilment_status === "delivered" || !!o.guarantee_expires_at;
            const claim = o.claim;
            const resolved = claim && ["approved", "denied"].includes(claim.status);
            const left = resolved ? null : windowLeft(o.guarantee_expires_at);
            const cc = claim ? CLAIM_COPY[claim.status] : null;
            const track = trackingUrl(o.carrier, o.tracking_number);
            return (
              <li key={o.id} className="py-6" data-testid={`order-row-${o.id}`}>
                <div className="flex items-start gap-4">
                  <Link to={`/product/${o.listing_id}`} className="shrink-0">
                    <div className="w-16 h-16 hairline overflow-hidden">
                      <ImageThumb path={o.image_path} className="w-full h-full object-cover" />
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base truncate">{o.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {o.brand ? `${o.brand} · ` : ""}from {o.seller_name || "seller"} · ordered {new Date(o.paid_at).toLocaleDateString()}
                    </div>
                    {o.buyer_shipping_service && (
                      <div className="text-xs text-neutral-500 mt-0.5" data-testid={`order-service-${o.id}`}>
                        Shipping: {o.buyer_shipping_carrier ? `${o.buyer_shipping_carrier} · ` : ""}{o.buyer_shipping_service}
                      </div>
                    )}
                    <div className="mt-3"><Timeline order={o} /></div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-[0.62rem] uppercase tracking-[0.16em]">
                      {o.tracking_number && (
                        track ? (
                          <a href={track} target="_blank" rel="noreferrer" data-testid={`track-link-${o.id}`}
                            className="inline-flex items-center gap-1 underline" style={{ color: "#2563EB" }}>
                            {o.carrier || "Tracking"} {o.tracking_number} <ExternalLink size={10} />
                          </a>
                        ) : (
                          <span className="text-neutral-600" data-testid={`track-code-${o.id}`}>
                            {o.carrier || "Tracking"} {o.tracking_number}
                          </span>
                        )
                      )}
                      {o.inspected_before_shipping && (
                        <span className="inline-flex items-center gap-1" style={{ color: "#16A34A" }} data-testid={`inspected-${o.id}`}>
                          <ShieldCheck size={11} /> Inspected before shipping
                        </span>
                      )}
                      {delivered ? (
                        left ? (
                          <span className="inline-flex items-center gap-1" style={{ color: "#2563EB" }} data-testid={`guarantee-${o.id}`}>
                            <Clock size={11} /> Guarantee {left}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-neutral-400" data-testid={`guarantee-${o.id}`}>
                            <ShieldCheck size={11} /> {resolved ? "Claim resolved" : "Guarantee window closed"}
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 text-neutral-600" data-testid={`guarantee-${o.id}`}>
                          <Clock size={11} /> Guarantee starts on delivery
                        </span>
                      )}
                      <span className="text-neutral-600 font-medium">Payout {o.payout_status}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-base">${Number(o.amount).toFixed(2)}</div>
                    <div className="mt-2 flex flex-col gap-2 items-end">
                      {!delivered && (
                        <button className="btn-klein text-[0.62rem]" data-testid={`received-btn-${o.id}`}
                          onClick={() => confirmReceipt(o.id)}>
                          <PackageCheck size={12} /> It arrived
                        </button>
                      )}
                      {delivered && !claim && left && (
                        <button className="btn-outline text-[0.62rem]" data-testid={`claim-btn-${o.id}`}
                          onClick={() => setClaimFor(o)}>
                          <Flag size={12} /> Report a fake
                        </button>
                      )}
                      {delivered && (
                        o.reviewed ? (
                          <span className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.14em]"
                            style={{ color: "#16A34A" }} data-testid={`reviewed-${o.id}`}>
                            <ThumbsUp size={12} /> Reviewed
                          </span>
                        ) : (
                          <button className="btn-outline text-[0.62rem]" data-testid={`review-btn-${o.id}`}
                            onClick={() => { setReview({ sentiment: "good", comment: "" }); setReviewFor(o); }}>
                            <ThumbsUp size={12} /> Rate seller
                          </button>
                        )
                      )}
                      {delivered && o.payout_status === "refunded" && (
                        <span className="text-[0.7rem] text-neutral-500 text-right max-w-[170px] leading-tight"
                          data-testid={`refund-feedback-note-${o.id}`}>
                          Fully refunded — you can still leave positive feedback.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {claim && (
                  <div className="mt-4 ml-20 hairline p-4" data-testid={`claim-status-${o.id}`} style={{ borderColor: cc?.color }}>
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={13} style={{ color: cc?.color }} />
                      <span className="label-eyebrow" style={{ color: cc?.color }}>{cc?.label || claim.status}</span>
                    </div>
                    <div className="mt-2 text-xs text-neutral-500">You reported: {claim.reason}</div>
                    {claim.ai?.reasoning && <p className="mt-2 text-sm text-neutral-600">{claim.ai.reasoning}</p>}
                    {claim.ai?.decision === "inconclusive" && (
                      <p className="mt-1 text-xs text-neutral-500" data-testid={`claim-inconclusive-${o.id}`}>
                        The evidence was inconclusive, so we sided with you — the seller keeps a clean record.
                      </p>
                    )}
                    {claim.seller_response && (
                      <p className="mt-2 text-xs text-neutral-500">Seller replied: “{claim.seller_response}”</p>
                    )}
                    {["assessing", "reassessing"].includes(claim.status) && (
                      <button className="chip mt-3" onClick={load} data-testid={`refresh-claim-${o.id}`}>Refresh result</button>
                    )}
                    {claim.status === "approved" && (
                      <div className="mt-3" data-testid={`return-label-block-${o.id}`}>
                        {o.return_label_url ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <a href={o.return_label_url} target="_blank" rel="noreferrer"
                              className="btn-primary inline-flex text-[0.62rem]" data-testid={`return-label-link-${o.id}`}>
                              Print return label{o.return_tracking_number ? ` · ${o.return_tracking_number}` : ""}
                            </a>
                            {o.return_tracking_url && (
                              <a href={o.return_tracking_url} target="_blank" rel="noreferrer"
                                className="btn-outline inline-flex text-[0.62rem]" data-testid={`return-track-link-${o.id}`}>
                                Track return
                              </a>
                            )}
                          </div>
                        ) : (
                          <button className="btn-primary text-[0.62rem] inline-flex items-center gap-1.5"
                            disabled={returnLoading === o.id} onClick={() => buyReturnLabel(o.id)}
                            data-testid={`return-label-btn-${o.id}`}>
                            {returnLoading === o.id ? "Creating label…" : "Print prepaid return label"}
                          </button>
                        )}
                        {o.return_tracking_status ? (
                          <p className="mt-1.5 text-[0.7rem] text-neutral-700" data-testid={`return-tracking-${o.id}`}>
                            Return status: <strong>{RETURN_STATUS_LABEL[o.return_tracking_status] || o.return_tracking_status}</strong>
                          </p>
                        ) : (
                          <p className="mt-1.5 text-[0.62rem] text-neutral-500">
                            Ship the item back with this prepaid label to complete your refund.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {claimFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setClaimFor(null)}>
          <div className="bg-white hairline p-8 w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="claim-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Authenticity claim</div>
                <h2 className="font-display text-2xl mt-1">{claimFor.title}</h2>
              </div>
              <button onClick={() => setClaimFor(null)} className="p-1" data-testid="claim-close"><X size={16} /></button>
            </div>
            <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
              Photograph what arrived — tags, logo, stitching, hardware. Our AI compares it to the original listing and
              the seller's proof. If it can't be settled, we side with you.
            </p>
            <div className="mt-5">
              <div className="label-eyebrow mb-2">What's wrong?</div>
              <select className="w-full hairline p-3 text-sm bg-white" data-testid="claim-reason"
                value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}>
                {REPORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-2">Details</div>
              <textarea rows={3} className="w-full hairline p-3 text-sm" data-testid="claim-note"
                value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            </div>
            <div className="mt-4">
              <div className="label-eyebrow mb-2">Photos of what arrived</div>
              <PhotoUpload paths={form.evidence_paths} testid="claim-evidence"
                onChange={(p) => setForm((f) => ({ ...f, evidence_paths: p }))} cta="Add your photos" />
            </div>
            <button disabled={sending} onClick={fileClaim} className="btn-primary w-full mt-6" data-testid="claim-submit">
              {sending ? "Filing…" : "File claim & hold payout"}
            </button>
          </div>
        </div>
      )}

      {reviewFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReviewFor(null)}>
          <div className="bg-white hairline p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Rate your seller</div>
                <h2 className="font-display text-2xl mt-1">{reviewFor.seller_name || "Seller"}</h2>
              </div>
              <button onClick={() => setReviewFor(null)} className="p-1" data-testid="review-close"><X size={16} /></button>
            </div>
            <p className="mt-2 text-xs text-neutral-500 leading-relaxed">
              How was “{reviewFor.title}”? Your feedback helps other buyers shop with confidence.
            </p>
            <div className="mt-5"><SentimentPicker value={review.sentiment} disableBad={reviewFor.payout_status === "refunded"} onChange={(s) => setReview((v) => ({ ...v, sentiment: s }))} /></div>
            <textarea rows={4} className="w-full hairline p-3 text-sm mt-4" placeholder="Share a few words (optional)"
              data-testid="review-comment" value={review.comment}
              onChange={(e) => setReview((v) => ({ ...v, comment: e.target.value }))} />
            <button disabled={savingReview} onClick={submitReview} className="btn-primary w-full mt-5" data-testid="review-submit">
              {savingReview ? "Posting…" : "Post review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
